import type { ReactNode } from "react";
import type { StockItem } from "../../stock/model";

export type Column = {
  key: string;
  label: ReactNode;
  /** Shown when hovering the heading. */
  title?: string;
  /** Visibility by the grid's own width (it narrows when an item is open beside it), such as "hidden @xl:table-cell". */
  className?: string;
  align?: "left" | "right" | "center";
  sort?: (item: StockItem) => number | string | null;
  render: (item: StockItem) => ReactNode;
};
export type Group = { key: string; label: string; note?: ReactNode; items: StockItem[] };
export type Sort = { key: string; dir: 1 | -1 } | null;

const alignClass = { left: "text-left", right: "text-right", center: "text-center" };

/** The master list: a table grouped under headings, sortable by any column with a sort, rows open the detail. */
export default function StockGrid({
  columns,
  groups,
  selectedId,
  onSelect,
  sort,
  onSort,
  empty,
}: {
  columns: Column[];
  groups: Group[];
  selectedId: string | null;
  onSelect: (item: StockItem) => void;
  sort: Sort;
  onSort: (key: string) => void;
  empty: ReactNode;
}) {
  const sorted = (items: StockItem[]) => {
    const column = sort && columns.find((c) => c.key === sort.key);
    if (!column?.sort) return items;
    return [...items].sort((a, b) => {
      const x = column.sort!(a);
      const y = column.sort!(b);
      // Blanks last, whichever way.
      if (x === null || x === "") return y === null || y === "" ? 0 : 1;
      if (y === null || y === "") return -1;
      return (x < y ? -1 : x > y ? 1 : 0) * sort!.dir;
    });
  };
  const rows = groups.filter((g) => g.items.length);

  return (
    <div className="@container overflow-hidden rounded-[20px] border border-line-soft bg-white shadow-card">
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0 text-[14px]">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  title={c.title}
                  aria-sort={sort?.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : undefined}
                  className={`whitespace-nowrap border-b border-line bg-[#fdf7f8] px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint first:pl-4 last:pr-4 ${alignClass[c.align ?? "left"]} ${c.className ?? ""}`}
                >
                  {c.sort ? (
                    <button onClick={() => onSort(c.key)} className={`inline-flex items-center gap-1 uppercase ${sort?.key === c.key ? "text-rose-ink" : ""}`}>
                      {c.label}
                      <span aria-hidden="true" className={sort?.key === c.key ? "" : "opacity-0"}>{sort?.dir === -1 ? "↓" : "↑"}</span>
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          {rows.map((group) => (
            <tbody key={group.key}>
              {rows.length > 1 && (
                <tr>
                  <th colSpan={columns.length} scope="colgroup" className="border-b border-line-soft bg-blush-soft px-4 py-1.5 text-left text-[12px] font-bold text-rose-ink">
                    {group.label} <span className="font-semibold text-ink-faint">· {group.items.length}</span>
                    {group.note && <span className="float-right font-semibold text-ink-faint">{group.note}</span>}
                  </th>
                </tr>
              )}
              {sorted(group.items).map((item) => {
                const selected = item.id === selectedId;
                return (
                  <tr
                    key={item.id}
                    onClick={() => onSelect(item)}
                    className={`cursor-pointer transition-colors ${selected ? "bg-blush" : "hover:bg-[#fdf5f6]"}`}
                    aria-selected={selected}
                  >
                    {columns.map((c, i) => (
                      <td
                        key={c.key}
                        className={`border-b border-line-soft px-3 py-2.5 align-middle first:pl-4 last:pr-4 ${alignClass[c.align ?? "left"]} ${c.className ?? ""} ${
                          i === 0 && selected ? "shadow-[inset_3px_0_0_#d8647a]" : ""
                        }`}
                      >
                        {c.render(item)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      </div>
      {!rows.length && <div className="px-4 py-10 text-center text-[14px] text-ink-faint">{empty}</div>}
    </div>
  );
}
